/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastData } from 'src/luis/core/stores/ToastStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TranslationJobStore, TranslationStatusType } from 'src/translation/stores/TranslationJobStore';

const eventBus = getEventBus();
const messages = defineMessages({
    failTitle: {
        id: 'useTranslationJobTracker.failTitle',
        defaultMessage: 'Translation job failed'
    },
    failDetails: {
        id: 'useTranslationJobTracker.failDetails',
        defaultMessage: 'Your translation job has failed. View the job in History to see details'
    },
    viewJobLink: {
        id: 'useTranslationJobTracker.viewJobLink',
        defaultMessage: 'View job in History'
    },
    startedTitle: {
        id: 'useTranslationJobTracker.startedTitle',
        defaultMessage: 'Translation job started'
    },
    startedDetails: {
        id: 'useTranslationJobTracker.startedDetails',
        defaultMessage:
            'Your translation job has started. Translation time is dependant on how much data you are translating and how many languages you are translating to'
    },
    successTitle: {
        id: 'useTranslationJobTracker.successTitle',
        defaultMessage: 'Translation job succeeded'
    },
    successDetails: {
        id: 'useTranslationJobTracker.successDetails',
        defaultMessage:
            'Your translation job has finished successfully and your translated documents sent to the selected destination. You can view translation jobs to see more details.'
    }
});

const getToastDataFromJob = ({
    jobId,
    localize,
    translationJobStatus,
    onTranslationJobClick
}: {
    jobId: string;
    onTranslationJobClick: (jobId: string) => void;
    translationJobStatus: TranslationStatusType;
    localize: ReactIntl.InjectedIntl['formatMessage'];
}): ToastData => {
    switch (translationJobStatus) {
        case 'NotStarted':
        case 'Running':
            return {
                status: 'running',
                title: localize(messages.startedTitle),
                content: localize(messages.startedDetails),
                actions: [{ label: localize(messages.viewJobLink), onClick: () => onTranslationJobClick(jobId) }]
            };
        case 'Succeeded':
            return {
                status: 'completed',
                content: localize(messages.successDetails),
                title: localize(messages.successTitle),
                actions: [{ label: localize(messages.viewJobLink), onClick: () => onTranslationJobClick(jobId) }]
            };
        case 'Failed':
        case 'ValidationFailed':
            return {
                status: 'failed',
                content: localize(messages.failDetails),
                title: localize(messages.failTitle),
                actions: [{ label: localize(messages.viewJobLink), onClick: () => onTranslationJobClick(jobId) }]
            };
        default:
            return {};
    }
};

export const useTranslationJobTracker = (
    translationJobStoreHouse: ResourceStoreHouse<TranslationJobStore>,
    onTranslationJobClick: (jobId: string) => void
) => {
    const { localize } = useLocalization();
    const toastIdRef = React.useRef<string>();
    const { rootStore, dispatcher } = useGlobalState();

    React.useEffect(() => {
        if (!translationJobStoreHouse) {
            return;
        }

        const disposer = autorun(() => {
            const toastTranslationJob = translationJobStoreHouse.items.find(j => j.id === toastIdRef.current);
            const runningTranslationJob = translationJobStoreHouse.items.find(j => j.status === 'NotStarted' || j.status === 'Running');

            if (!runningTranslationJob && !toastTranslationJob) {
                return;
            }

            const currentTranslationJob = runningTranslationJob ?? toastTranslationJob;

            const toastData = getToastDataFromJob({
                localize,
                onTranslationJobClick,
                jobId: currentTranslationJob.id,
                translationJobStatus: currentTranslationJob.status
            });

            if (toastIdRef.current !== currentTranslationJob.id) {
                toastIdRef.current = currentTranslationJob.id;
                eventBus.emit('addToast', { id: toastIdRef.current, ...toastData });
            } else {
                eventBus.emit('updateToast', { id: toastIdRef.current, toast: toastData });
            }
        });

        return () => {
            disposer();
            if (rootStore.toastStoreHouse.findById(toastIdRef.current)) {
                dispatcher.dispatch('removeToast', { id: toastIdRef.current });
            }
        };
    }, [translationJobStoreHouse]);
};
